<template>
    <div class="login">
        <h1>Sign in to MyStekz</h1>
        <div style="background: white">
            <Message v-if="errorMessage" :closable="false" severity="error">
                {{ errorMessage }}
            </Message>
            <Message v-if="successMessage" :closable="false" severity="success">
                {{ successMessage }}
            </Message>
        </div>
        <div v-if="showEmailInput" class="login-inputs">
            <div class="input email">
                <label for="email">Email</label>
                <InputText v-model="email" />
            </div>

            <div class="buttons">
                <Button
                    class="p-button-raised button"
                    label="Send Login Link"
                    @click="sendMagicLink"
                />
            </div>
        </div>

        <div v-else-if="showEmailPasswordInputs" class="login-inputs">
            <form>
                <div class="input email">
                    <label for="email">Email</label>
                    <InputText v-model="email" autocomplete="username" />
                </div>
                <div class="input password">
                    <label for="password">Password</label>
                    <Password
                        v-model="password"
                        :inputProps="{
                            autocomplete: 'current-password',
                        }"
                        :fluid="true"
                        :feedback="false"
                        @keydown.enter="signInWithEmail"
                    />
                </div>

                <div class="forgot-password-link">
                    <router-link to="/forgot-password"
                        >Forgot Password?</router-link
                    >
                </div>

                <div class="buttons">
                    <Button
                        class="p-button-raised button"
                        label="Sign in"
                        @click="signInWithEmail"
                    >
                    </Button>
                </div>
            </form>
        </div>

        <div v-else class="buttons">
            <p class="choose-method">Choose your sign in method.</p>
            <Button
                class="p-button-raised button"
                label="Sign in with Google"
                @click="signInWithGoogle"
            >
                <template #icon>
                    <img
                        class="google-icon"
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    />
                </template>
            </Button>
            <Button
                class="p-button-raised button"
                label="Sign in with Email"
                icon="pi pi-envelope"
                @click="showEmailPasswordInputs = true"
            >
                <template #icon>
                    <img
                        class="mail-icon"
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/mail.svg"
                    />
                </template>
            </Button>
        </div>
        <div class="signup-link">
            Don't have an account yet?
            <router-link to="/signup">Sign up</router-link>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const showEmailPasswordInputs = ref(false);
const showEmailInput = ref(false);
const isMagicLink = ref(false);
const errorMessage = ref("");
const successMessage = ref("");
const email = ref("");
const password = ref("");

const router = useRouter();
const route = useRoute();
const {
    isSignInWithEmailLink,
    sendSignInLinkToEmail,
    signInWithEmailLink,
    signInWithEmailAndPassword,
    signInWithGoogle,
} = useAuthenticationStore();
const { authStatus } = useAuthenticationManager();

const signInWithEmail = async () => {
    errorMessage.value = "";
    try {
        await signInWithEmailAndPassword(email.value, password.value);
    } catch {
        errorMessage.value = "Incorrect email/password combination";
    }
};

const sendMagicLink = async () => {
    errorMessage.value = "";
    try {
        await sendSignInLinkToEmail(email.value);
        successMessage.value = "Magic link sent to your email address.";
    } catch (e) {
        errorMessage.value = (e as Error).message;
    }
};

/**
 * Handle magic email links.
 */
watch(
    route,
    async () => {
        if (isSignInWithEmailLink()) {
            isMagicLink.value = true;
            errorMessage.value = "";
            successMessage.value =
                "Logging you in. You will be redirected soon.";
            try {
                await signInWithEmailLink();
            } catch (e) {
                isMagicLink.value = false;
                errorMessage.value = (e as Error).message;
            }
        }
    },
    { immediate: true }
);

/**
 * A sanitized version of the returnTo query parameter.
 * This is used to redirect the user after sign in.
 * Can only returnTo by route name (and params and query), nothing else.
 */
const returnTo = computed(() => {
    try {
        // Expect returnTo to be a JSON string containing {name: string, params?: object, query?: object}
        const routeData = JSON.parse(
            decodeURIComponent(route.query.returnTo as string)
        );

        // Validate the route name exists
        if (
            typeof routeData.name !== "string" ||
            !router.hasRoute(routeData.name)
        ) {
            return "";
        }

        // Resolve the route with provided params and query
        const to = router.resolve({
            name: routeData.name,
            params: routeData.params || {},
            query: routeData.query || {},
        });

        return to.fullPath;
    } catch {
        return "";
    }
});

/**
 * Redirect after sign in.
 */
watch(
    authStatus,
    async (newAuthStatus) => {
        if (newAuthStatus === AuthStatus.SIGNED_IN) {
            successMessage.value = "Redirecting...";
            router.push(returnTo.value || "/");
        }
    },
    { immediate: true }
);
</script>

<style lang="scss" scoped>
div.login {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    height: 100vh;
    padding: 30px;
    align-items: center;
    color: var(--stekz-white);
    background-color: var(--stekz-primary-color);

    h1 {
        color: var(--stekz-white);
    }

    > div {
        max-width: 320px;
    }
}

.buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 20px;
}

.button {
    background-color: var(--stekz-dev-color);
    color: var(--stekz-white);
    width: 300px;
}

button.back-button,
button.firebase-login,
button.email-password-login {
    margin-top: 10px;
    color: var(--stekz-white);
    background-color: var(--stekz-dev-color);
    direction: ltr;
    font-weight: 500;
    height: auto;
    line-height: normal;
    min-height: 40px;
    padding: 8px 16px;
    text-align: center;
    width: 100%;
    position: relative;
    height: 36px;
    min-width: 64px;
    padding: 0 16px;
    display: inline-block;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-size: 14px;
    letter-spacing: 0;
    overflow: hidden;
    outline: none;
    text-decoration: none;
    vertical-align: middle;
    border: 1px #757575 solid;
    border-radius: 3px;
}

button.firebase-login > span {
    display: table-cell;
}

#otp {
    justify-content: center;
    margin: 2rem auto;
}

img.google-icon {
    border: none;
    display: inline-block;
    height: 18px;
    vertical-align: middle;
    width: 18px;
}

.login-inputs > div.input {
    margin-bottom: 0.5rem;
}

.login-inputs .buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-top: 20px;
}

.input {
    &.email,
    &.password {
        display: flex;
        flex-direction: column;
    }
}

.choose-method {
    text-align: center;
    margin-bottom: 0.5rem;
}

.signup-link {
    text-align: center;
    margin-top: 2rem;

    a {
        color: var(--stekz-white);
        text-decoration: underline;

        &:hover {
            text-decoration: none;
        }
    }
}

.forgot-password-link {
    text-align: right;
    margin-bottom: 1rem;
    font-size: 0.9rem;

    a {
        color: var(--stekz-white);
        text-decoration: underline;
    }
}
</style>
